<template>
  <v-dialog
    :model-value="modelValue"
    max-width="720"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="pa-4">
      <div class="d-flex align-center mb-3">
        <span class="text-h6 mr-auto">{{ $t("workshop.camera.title") }}</span>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          :title="$t('workshop.camera.cancel')"
          :aria-label="$t('workshop.camera.cancel')"
          @click="close"
        />
      </div>

      <v-alert
        v-if="errorKey"
        type="warning"
        variant="tonal"
        density="compact"
        class="mb-3"
      >
        {{ $t(errorKey) }}
      </v-alert>
      <div v-else class="workshop-camera-stage">
        <video ref="video" autoplay playsinline muted />
        <v-progress-circular
          v-if="!ready"
          indeterminate
          color="white"
          class="workshop-camera-wait"
        />
      </div>

      <div class="d-flex justify-end ga-2 mt-4">
        <v-btn variant="text" @click="close">
          {{ $t("workshop.camera.cancel") }}
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          prepend-icon="mdi-camera"
          :disabled="!ready || taking"
          :loading="taking"
          @click="take"
        >
          {{ $t("workshop.camera.take") }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";

// Webcam capture for the workshop evidence on desktop (Stage 3a slice 6). Phones use the native
// camera input instead (workshopTaskCard). Opens the camera while the dialog is open, takes one
// JPEG snapshot and emits it as a File ("capture"); the camera stops on close and on unmount.
export default {
  name: "WorkshopCameraDialog",
  props: {
    modelValue: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "capture"],
  data: () => ({
    ready: false,
    taking: false,
    // workshop.camera.* key when the camera cannot be used
    errorKey: null,
  }),
  watch: {
    modelValue: {
      immediate: true,
      handler(open) {
        if (open) this.start();
        else this.stop();
      },
    },
  },
  created() {
    // Not reactive: the MediaStream of the open camera.
    this.stream = null;
    // Only the latest start may keep its stream.
    this.startSeq = 0;
  },
  beforeUnmount() {
    this.stop();
  },
  methods: {
    async start() {
      const seq = (this.startSeq += 1);
      this.ready = false;
      this.errorKey = null;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: "environment",
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });
        // Closed (or reopened) meanwhile.
        if (seq !== this.startSeq || !this.modelValue) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        this.stream = stream;
        await this.$nextTick();
        const video = this.$refs.video;
        video.srcObject = stream;
        video.onloadedmetadata = () => {
          if (seq === this.startSeq) this.ready = true;
        };
      } catch (err) {
        if (seq !== this.startSeq) return;
        this.errorKey =
          err && err.name === "NotAllowedError"
            ? "workshop.camera.denied"
            : "workshop.camera.unavailable";
      }
    },
    stop() {
      this.startSeq += 1;
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
        this.stream = null;
      }
      if (this.$refs.video) this.$refs.video.srcObject = null;
      this.ready = false;
      this.taking = false;
    },
    // One frame at the camera's own resolution, as a JPEG file.
    take() {
      const video = this.$refs.video;
      if (!this.ready || !video || !video.videoWidth) return;
      this.taking = true;
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext("2d").drawImage(video, 0, 0);
      canvas.toBlob(
        (blob) => {
          this.taking = false;
          if (!blob) {
            this.errorKey = "workshop.camera.unavailable";
            return;
          }
          const name = `foto-${moment().format("YYYYMMDD-HHmmss")}.jpg`;
          this.$emit(
            "capture",
            new File([blob], name, {
              type: "image/jpeg",
              lastModified: Date.now(),
            }),
          );
          this.close();
        },
        "image/jpeg",
        0.9,
      );
    },
    close() {
      this.$emit("update:modelValue", false);
    },
  },
};
</script>

<style scoped>
.workshop-camera-stage {
  position: relative;
  background: #000;
  border-radius: 4px;
  overflow: hidden;
  aspect-ratio: 16 / 9;
}

.workshop-camera-stage video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.workshop-camera-wait {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
</style>
